import { useEffect, useState } from "react";

/**
 * The time at the ground.
 *
 * Fixed to Asia/Karachi rather than the device clock. A scorer's phone is
 * normally on Pakistan time anyway, but a watcher's may not be — and a match
 * clock that reads differently depending on who is looking is worse than no
 * clock. Karachi has no daylight saving, so the offset never moves.
 *
 * Ticks on the minute, not the second: a seconds display costs a render a
 * second on the screen that most needs to stay smooth, and nobody scores
 * cricket to the second.
 */
const FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Karachi",
  weekday: "short", day: "numeric", month: "short",
  hour: "2-digit", minute: "2-digit", hour12: true,
});

export function nowInKarachi(at: Date = new Date()): string {
  // "Mon 27 Jul, 10:15 pm" — day first, which is how it is written here.
  return FMT.format(at).replace(",", "").replace(/\s(am|pm)/i, (m) => m.toLowerCase());
}

/**
 * Split into the part that must always be visible and the part that can go.
 *
 * A 320px phone could not fit the full string beside a logout button — the
 * header overflowed by 44px and pushed the button off the screen entirely. The
 * time is what a clock is for; the date is the part to drop.
 */
export function karachiParts(at: Date = new Date()): { date: string; time: string } {
  const full = nowInKarachi(at);
  const m = /^(.*?)\s(\d{1,2}:\d{2}\s*[ap]m)$/i.exec(full);
  return m ? { date: m[1]!.trim(), time: m[2]!.trim() } : { date: "", time: full };
}

export function Clock({ className = "" }: { className?: string }) {
  const [parts, setParts] = useState(() => karachiParts());

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setParts(karachiParts());
      // Line up with the next minute boundary rather than drifting a second
      // later on every pass.
      const ms = 60_000 - (Date.now() % 60_000);
      timer = setTimeout(tick, ms + 50);
    };
    const ms = 60_000 - (Date.now() % 60_000);
    timer = setTimeout(tick, ms + 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <span className={`shrink-0 font-mono text-meta tabular-nums text-faint ${className}`}
          title="Pakistan Standard Time">
      {/* The date is the first thing to go when the header runs out of room. */}
      <span className="hidden split:inline">{parts.date} </span>
      {parts.time} <span className="opacity-60">PKT</span>
    </span>
  );
}
